'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { strings } from '@/content/strings';

type ChatMessage = {
  id: string;
  senderType: 'guest' | 'host' | 'admin' | 'system';
  senderName?: string | null;
  body: string;
  createdAt: string;
};

type ConversationState = {
  conversationId: string;
  guestName: string;
  guestEmail?: string;
  guestPhone?: string;
  propertyId?: string | null;
};

type ChatContext = {
  propertyId?: string;
  propertyName?: string;
  propertySlug?: string;
};

const STORAGE_PREFIX = 'rm_chat_state_v1';

export default function ChatWidget() {
  const chatStrings = strings.common.chat;
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState<ChatContext | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const isHidden = pathname.startsWith('/admin') || pathname.startsWith('/login');

  const storageKey = useMemo(() => {
    const suffix = context?.propertyId ? `_${context.propertyId}` : '_global';
    return `${STORAGE_PREFIX}${suffix}`;
  }, [context?.propertyId]);

  useEffect(() => {
    if (isHidden) return;
    const el = document.querySelector('[data-chat-context="property"]') as HTMLElement | null;
    if (el) {
      setContext({
        propertyId: el.dataset.propertyId || undefined,
        propertyName: el.dataset.propertyName || undefined,
        propertySlug: el.dataset.propertySlug || undefined,
      });
    } else {
      setContext(null);
    }
  }, [pathname, isHidden]);

  useEffect(() => {
    if (isHidden) return;
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        setConversationId(null);
        setMessages([]);
        return;
      }
      const stored = JSON.parse(raw) as ConversationState;
      setConversationId(stored.conversationId);
      setGuestName(stored.guestName || '');
      setGuestEmail(stored.guestEmail || '');
      setGuestPhone(stored.guestPhone || '');
    } catch {
      setConversationId(null);
      setMessages([]);
    }
  }, [storageKey, isHidden]);

  useEffect(() => {
    if (!open || !conversationId || isHidden) return;
    void loadMessages(conversationId);
  }, [open, conversationId, isHidden]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const persistState = (state: ConversationState) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // ignore
    }
  };

  const loadMessages = async (id: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/chat/conversations/${id}/messages`);
      const data = await res.json();
      if (!data.success) {
        setError(data.error || chatStrings.loadMessagesError);
        return;
      }
      setMessages(
        (data.data as ChatMessage[]).map((msg) => ({
          ...msg,
          createdAt: new Date(msg.createdAt).toISOString(),
        }))
      );
    } catch (err) {
      setError(chatStrings.loadMessagesError);
    } finally {
      setLoading(false);
    }
  };

  const startConversation = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/chat/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyId: context?.propertyId,
          guestName,
          guestEmail,
          guestPhone,
          message: draft,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.error || chatStrings.startChatError);
        return;
      }
      const conversation = data.data.conversation as { id: string };
      setConversationId(conversation.id);
      setMessages(
        (data.data.messages as ChatMessage[]).map((msg) => ({
          ...msg,
          createdAt: new Date(msg.createdAt).toISOString(),
        }))
      );
      persistState({
        conversationId: conversation.id,
        guestName,
        guestEmail,
        guestPhone,
        propertyId: context?.propertyId ?? null,
      });
      setDraft('');
    } catch (err) {
      setError(chatStrings.startChatError);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!conversationId || !draft.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/chat/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: draft }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.error || chatStrings.sendMessageError);
        return;
      }
      setMessages((prev) => [...prev, { ...data.data, createdAt: new Date(data.data.createdAt).toISOString() }]);
      setDraft('');
    } catch (err) {
      setError(chatStrings.sendMessageError);
    } finally {
      setLoading(false);
    }
  };

  if (isHidden) {
    return null;
  }

  const headerTitle = context?.propertyName
    ? chatStrings.headerWithProperty(context.propertyName)
    : chatStrings.headerDefault;
  const buttonLabel = open ? chatStrings.buttonClose : chatStrings.buttonOpen;

  return (
    <div className="fixed bottom-6 right-6 z-50" data-chat-widget>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="rounded-full bg-primary text-primary-foreground px-5 py-3 shadow-lg text-sm font-semibold hover:opacity-90"
      >
        {buttonLabel}
      </button>

      {open && (
        <div className="mt-3 w-[320px] sm:w-[360px] rounded-2xl border border-border bg-card text-card-foreground shadow-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-foreground">{headerTitle}</div>
              <div className="text-xs text-muted-foreground">{chatStrings.replyTime}</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
              ✕
            </button>
          </div>

          {conversationId ? (
            <div className="flex flex-col h-[380px]">
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {messages.length === 0 && !loading && (
                  <div className="text-xs text-muted-foreground">{chatStrings.noMessages}</div>
                )}
                {messages.map((message) => {
                  const isGuest = message.senderType === 'guest';
                  return (
                    <div key={message.id} className={`flex ${isGuest ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[80%] rounded-2xl px-3 py-2 text-xs leading-relaxed ${
                          isGuest
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-ghost text-ghost-foreground'
                        }`}
                      >
                        {!isGuest && message.senderName && (
                          <div className="text-[11px] font-semibold mb-1">{message.senderName}</div>
                        )}
                        <div>{message.body}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {error && <div className="px-4 text-xs text-destructive">{error}</div>}
              <div className="border-t border-border p-3 space-y-2">
                <textarea
                  rows={2}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={chatStrings.messagePlaceholder}
                  className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <button
                  type="button"
                  onClick={sendMessage}
                  disabled={loading || !draft.trim()}
                  className="w-full rounded-lg bg-primary text-primary-foreground py-2 text-xs font-semibold disabled:opacity-50"
                >
                  {chatStrings.sendMessage}
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              <div className="space-y-2">
                <input
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder={chatStrings.namePlaceholder}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <input
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder={chatStrings.emailPlaceholder}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <input
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  placeholder={chatStrings.phonePlaceholder}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <textarea
                rows={3}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={chatStrings.helpPlaceholder}
                className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
              />
              {error && <div className="text-xs text-destructive">{error}</div>}
              <button
                type="button"
                onClick={startConversation}
                disabled={loading || !guestName.trim() || (!guestEmail.trim() && !guestPhone.trim()) || !draft.trim()}
                className="w-full rounded-lg bg-primary text-primary-foreground py-2 text-xs font-semibold disabled:opacity-50"
              >
                {chatStrings.startChat}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
